import { ref } from "vue";
import { $http } from "@/plugins/api";
import type { Collection, Params } from "@/types/api";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import type { TrendChartData } from "@/modules/SecurityAwarnessTraining/types/trend-chart";
import { getApiClientUrl } from "@/config/runtimeConfig";

interface Customer {
  customerName: string;
  customerOrganisationID: string;
}

export function useTrendChart() {
  const { mspGlobalOrganisationId } = useGlobalOrganisationID();
  const customers = ref<Customer[]>([]);
  const isMore = ref<boolean>(true);
  const chartData = ref<TrendChartData[]>([]);

  const getCustomers = async ({
    params,
  }: {
    params: Record<string, unknown>;
  }): Promise<void> => {
    if (mspGlobalOrganisationId.value === null) {
      throw new Error("Organisation ID is not provided");
    }
    const reqParams: Params = {};

    for (const [key, value] of Object.entries(params)) {
      reqParams[key] = String(value).toLocaleLowerCase();
    }

    const searchParams = new URLSearchParams(reqParams);
    const res = await $http.get<Collection<Customer>>(
      `${getApiClientUrl()}/v1/cams/manager/sat/${mspGlobalOrganisationId.value}/stats/customers?${searchParams.toString()}`,
    );

    if (res.status === 200) {
      isMore.value =
        res.data.current_page * res.data.page_size < res.data.total;
      customers.value = res.data.results;
    }
  };

  const getMoreCustomers = async ({
    params,
  }: {
    params: Record<string, unknown>;
  }): Promise<void> => {
    if (mspGlobalOrganisationId.value === null) {
      throw new Error("Organisation ID is not provided");
    }

    const reqParams: Params = {};

    for (const [key, value] of Object.entries(params)) {
      reqParams[key] = String(value).toLocaleLowerCase();
    }

    const searchParams = new URLSearchParams(reqParams);

    const res = await $http.get<Collection<Customer>>(
      `${getApiClientUrl()}/v1/cams/manager/sat/${mspGlobalOrganisationId.value}/stats/customers?${searchParams.toString()}`,
    );

    if (res.status === 200) {
      const more = res.data.results;

      customers.value = [...customers.value, ...more];
      isMore.value =
        res.data.current_page * res.data.page_size < res.data.total;
    }
  };

  const getChartData = async ({
    params,
  }: {
    params: Record<string, unknown>;
  }) => {
    if (mspGlobalOrganisationId.value === null) {
      throw new Error("Organisation ID is not provided");
    }

    const reqParams: Params = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        reqParams[key] = String(value).toLocaleLowerCase();
      }
    }
    const searchParams = new URLSearchParams(reqParams);

    const res = await $http.get<TrendChartData[]>(
      `${getApiClientUrl()}/v1/cams/manager/sat/${mspGlobalOrganisationId.value}/stats/trend-chart?${searchParams.toString()}`,
    );

    if (res.status === 200) {
      chartData.value = res.data;
    }
  };

  return {
    customers,
    isMore,
    getCustomers,
    getMoreCustomers,
    chartData,
    getChartData,
  };
}
